import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Building2, Layers, Map,
  FileSignature, Wallet, Banknote,
} from "lucide-react";
import ClinqLogo from "../components/ClinqLogo";
import { trackPageView } from "../lib/tracker";
import { gaEvent } from "../lib/ga";
import {
  PROJECT_QUIZ_STORAGE_KEY, PROJECT_CATEGORIES, PROJECT_STAGES,
  LAND_OPTIONS, OFFTAKE_OPTIONS, EQUITY_OPTIONS, PROJECT_RAISE_BRACKETS,
  computeProjectPreliminaryScore, scoreProjectFunderPreview,
} from "../lib/projectQuiz";
import { QuizResults, PROJECT_COPY } from "../components/quiz/QuizResults";
import { QuizAuthGate } from "../components/quiz/QuizAuthGate";
import { ShareScore } from "../components/quiz/ShareScore";
import { ResetWelcomeBack } from "../components/quiz/ResetWelcomeBack";

const STEP_META = [
  { Icon: Building2, title: <>What type of <span className="text-emerald-400">project</span> are you raising capital for?</>, sub: "A project is a specific asset you're building — not the general growth of a trading business." },
  { Icon: Layers, title: <>What <span className="text-emerald-400">stage</span> is the project at?</>, sub: "Funders scale their expectations to your stage — be honest, it works in your favour." },
  { Icon: Map, title: <>What's the status of the <span className="text-emerald-400">land or site</span>?</>, sub: "Land control is the first thing every project funder screens on." },
  { Icon: FileSignature, title: <>How much future revenue is already <span className="text-emerald-400">committed</span>?</>, sub: "Signed offtake, PPA, pre-sales or pre-lets — the single biggest fundability differentiator." },
  { Icon: Wallet, title: <>How much of the project cost is <span className="text-emerald-400">your own money</span>?</>, sub: "Cash, land or work already in place. Funders co-invest — they don't fund 100%." },
  { Icon: Banknote, title: <>How much are you <span className="text-emerald-400">looking to raise</span>?</>, sub: "Funders have minimum and maximum ticket sizes — your raise amount decides which funder pools are a fit. A rough bracket is enough." },
];

export default function GetStartedProject() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const requiredRedirect = params.get("required") === "1" && !!user && user.role === "founder";
  const [saveErr, setSaveErr] = useState("");
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [stage, setStage] = useState("");
  const [land, setLand] = useState("");
  const [offtake, setOfftake] = useState("");
  const [equity, setEquity] = useState("");
  const [raiseAmount, setRaiseAmount] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => { trackPageView(`/get-started-project/step-${step}`); }, [step]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PROJECT_QUIZ_STORAGE_KEY);
      if (raw) {
        const o = JSON.parse(raw);
        if (o?.category) setCategory(o.category);
        if (o?.stage) setStage(o.stage);
        if (o?.land) setLand(o.land);
        if (o?.offtake) setOfftake(o.offtake);
        if (o?.equity) setEquity(o.equity);
        if (o?.raise) setRaiseAmount(o.raise);
      }
    } catch (_) { /* ignore */ }
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(PROJECT_QUIZ_STORAGE_KEY,
        JSON.stringify({ category, stage, land, offtake, equity, raise: raiseAmount }));
    } catch (_) { /* ignore */ }
  }, [hydrated, category, stage, land, offtake, equity, raiseAmount]);

  const canNext = { 1: !!category, 2: !!stage, 3: !!land, 4: !!offtake, 5: !!equity, 6: !!raiseAmount }[step];

  const draft = useMemo(() => ({ category, stage, land, offtake, equity, raise: raiseAmount }),
    [category, stage, land, offtake, equity, raiseAmount]);
  const preliminary = useMemo(() => computeProjectPreliminaryScore(draft), [draft]);
  const funderPreview = useMemo(() => scoreProjectFunderPreview(draft), [draft]);

  const saveCompleted = () => {
    try {
      localStorage.setItem(PROJECT_QUIZ_STORAGE_KEY, JSON.stringify({
        ...draft, preliminary_score: preliminary?.score ?? null,
        completed_at: new Date().toISOString(),
      }));
    } catch (_) { /* ignore */ }
  };

  const proceed = async () => {
    if (step === 7) {
      gaEvent("quiz_complete", { quiz: "get_started_project", score: preliminary?.score });
      saveCompleted();
      if (user && user.role === "founder") {
        try {
          await api.post("/project/bootstrap", { quiz: { ...draft, preliminary_score: preliminary?.score ?? null, completed_at: new Date().toISOString() } });
          try { localStorage.removeItem(PROJECT_QUIZ_STORAGE_KEY); } catch (_) { /* ignore */ }
        } catch (_) {
          setSaveErr("We couldn't save your answers — please check your connection and try again.");
          return;
        }
        await refresh();
        nav("/project/intake");
        return;
      }
      nav("/signup?role=project");
      return;
    }
    if (!canNext) return;
    if (step === 6) saveCompleted();
    setStep(step + 1);
  };

  const badgeClasses = (l) => (
    l === "high" ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-300"
      : l === "medium" ? "border-amber-400/40 bg-amber-400/15 text-amber-300"
      : "border-white/15 bg-white/[0.04] text-white/50"
  );
  const badgeLabel = (l) => ({ high: "Possible Fit", medium: "Worth Exploring", low: "Long-shot" }[l]);

  const OptCard = ({ opt, active, onClick, testid }) => (
    <button
      type="button" data-testid={testid} onClick={onClick}
      className={"flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all " +
        (active ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")}>
      <div className={"flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all " +
        (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")}>
        {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
      </div>
      <div className="min-w-0">
        <span className="text-sm font-semibold tracking-tight">{opt.label}</span>
        {opt.hint && <span className="block text-xs text-white/50">{opt.hint}</span>}
      </div>
    </button>
  );

  const hintsBlock = preliminary?.hints?.length ? (
    <div className="mt-10" data-testid="project-quiz-hints">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">What to strengthen first</p>
      <div className="mt-3 space-y-2.5">
        {preliminary.hints.map((h, i) => (
          <div key={i} className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#F59E0B]/20 text-[10px] font-bold text-[#F59E0B]">{i + 1}</span>
            <p className="text-sm text-white/80">{h}</p>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const meta = step <= 6 ? STEP_META[step - 1] : null;
  const MetaIcon = meta?.Icon;

  return (
    <div className="min-h-screen bg-[#04302E] text-white" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}>
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="block" data-testid="project-quiz-logo-home"><ClinqLogo /></Link>
          <Link to="/login" data-testid="project-quiz-signin-link" className="text-sm font-semibold text-white/70 hover:text-white">
            Sign in
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-3xl px-6 py-10 sm:py-16">
        {requiredRedirect && <ResetWelcomeBack noun="project" />}
        {requiredRedirect && (
          <div data-testid="project-quiz-required-banner"
            className="mb-8 rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-5 py-4 text-sm leading-relaxed text-amber-100">
            To give you the best advice on your project, we need to know just a bit more before you continue.
          </div>
        )}
        <div className="flex items-center justify-center gap-2" data-testid="project-quiz-progress">
          {[1, 2, 3, 4, 5, 6, 7].map((s) => (
            <span key={s} className={"h-1.5 w-10 rounded-full transition-all " + (s <= step ? "bg-[#F59E0B]" : "bg-white/15")} />
          ))}
        </div>
        <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
          Step {step} of 7 · Project Readiness Quiz
        </p>

        {step <= 6 ? (
          <section data-testid={`project-quiz-step-${step}`} className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <MetaIcon className="h-5 w-5 text-emerald-300" />
            </div>
            <h1 className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
                style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}>
              {meta.title}
            </h1>
            <p className="mt-4 text-base text-white/70">{meta.sub}</p>

            <div className={"mt-8 grid gap-2.5 " + (step === 1 || step === 2 || step === 6 ? "sm:grid-cols-2" : "")}>
              {step === 1 && PROJECT_CATEGORIES.map((o) => (
                <OptCard key={o.id} opt={o} active={category === o.id}
                  onClick={() => setCategory(o.id)} testid={`project-quiz-category-${o.id}`} />
              ))}
              {step === 2 && PROJECT_STAGES.map((o) => (
                <OptCard key={o.id} opt={o} active={stage === o.id}
                  onClick={() => setStage(o.id)} testid={`project-quiz-stage-${o.id}`} />
              ))}
              {step === 3 && LAND_OPTIONS.map((o) => (
                <OptCard key={o.id} opt={o} active={land === o.id}
                  onClick={() => setLand(o.id)} testid={`project-quiz-land-${o.id}`} />
              ))}
              {step === 4 && OFFTAKE_OPTIONS.map((o) => (
                <OptCard key={o.id} opt={o} active={offtake === o.id}
                  onClick={() => setOfftake(o.id)} testid={`project-quiz-offtake-${o.id}`} />
              ))}
              {step === 5 && EQUITY_OPTIONS.map((o) => (
                <OptCard key={o.id} opt={o} active={equity === o.id}
                  onClick={() => setEquity(o.id)} testid={`project-quiz-equity-${o.id}`} />
              ))}
              {step === 6 && PROJECT_RAISE_BRACKETS.map((o) => (
                <OptCard key={o.id} opt={o} active={raiseAmount === o.id}
                  onClick={() => setRaiseAmount(o.id)} testid={`project-quiz-raise-${o.id}`} />
              ))}
            </div>

            <div className="mt-10 flex items-center justify-between">
              {step > 1 ? (
                <button data-testid="project-quiz-back" onClick={() => setStep(step - 1)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              ) : <span />}
              <button data-testid="project-quiz-next" disabled={!canNext} onClick={proceed}
                className={"inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (canNext
                    ? (step === 6 ? "bg-[#F59E0B] text-[#04302E] shadow-lg shadow-black/30 hover:bg-amber-400"
                                  : "bg-[#0A8B85] text-white shadow-lg shadow-black/30 hover:bg-[#066667]")
                    : "cursor-not-allowed bg-white/10 text-white/40")}>
                {step === 6 ? "See my preliminary score" : "Next"}
                <ArrowRight className={"h-4 w-4 " + (step === 6 ? "" : "text-emerald-300")} />
              </button>
            </div>
          </section>
        ) : preliminary && !user ? (
          <>
            <section className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="project-quiz-teaser">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">Your preliminary result</p>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <span className="rounded-full border border-emerald-400/40 bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300" data-testid="project-quiz-teaser-band">{preliminary.band?.label || "Preliminary band"}</span>
                <span className="text-sm text-white/75">Estimated project readiness band — exact score revealed after sign-up.</span>
              </div>
              {funderPreview?.length ? (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-white/60">Funder types most likely to look at a project like yours</p>
                  <ul className="mt-2 flex flex-wrap gap-2" data-testid="project-quiz-teaser-funders">
                    {funderPreview.slice(0, 4).map((f, i) => (
                      <li key={i} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/85">
                        {f.label || f.name}<span className={"rounded-full border px-1.5 py-0.5 text-[9px] font-bold " + badgeClasses(f.likelihood)}>{badgeLabel(f.likelihood)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <p className="mt-4 text-xs text-white/60">Create a free account to see your exact score, the hard checks you pass or fail, and your next steps.</p>
            </section>
            <div className="mt-4">
              <QuizAuthGate ventureKind="project" noun="project" onBack={() => setStep(6)}
                onAuthed={async () => {
                  gaEvent("quiz_complete", { quiz: "get_started_project", score: preliminary?.score, gate: "auth" });
                  saveCompleted();
                  try {
                    await api.post("/project/bootstrap", { quiz: { ...draft, preliminary_score: preliminary?.score ?? null, completed_at: new Date().toISOString() } });
                  } catch (_) { /* the CTA retries and surfaces errors */ }
                  try { await refresh(); } catch (_) { /* ignore */ }
                }} />
            </div>
          </>
        ) : preliminary ? (
          <div data-testid="project-quiz-verdict">
            <QuizResults result={preliminary} funders={funderPreview} badgeClasses={badgeClasses} badgeLabel={badgeLabel}
              onBack={() => setStep(6)} onCta={proceed} saveErr={saveErr}
              copy={{ ...PROJECT_COPY, cta: "Continue to get my Project Readiness Report", reportTitle: "Get your Project Readiness Report" }}
              shareSlot={<ShareScore quiz="project" score={preliminary.score} bandLabel={preliminary.band.label} answers={draft} />}>
              {hintsBlock}
            </QuizResults>
          </div>
        ) : null}
      </div>
    </div>
  );
}
